import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, TextField, Typography } from "@mui/material";

import { useInfiniteQuery } from "@tanstack/react-query";
import useAuth from "../../../hooks/useAuth";
import { apiVariable } from "../../../api/apiVariable";
import axios from "axios";

import { relayPaymentsTableColumns } from "../../../data/FormColumns/Fleet/relayPaymentsTableColumns";
import PersistedDataGrid from "../../PersistedDataGrid/PersistedDataGrid";

const fetchRelayCodes = async ({
  token,
  company,
  offset,
  limit,
  searchTerm,
}: {
  token: string;
  company: string;
  offset: number;
  limit: number;
  searchTerm: string | null;
}) => {
  const params: any = { offset, limit, company };
  if (searchTerm) params.q = searchTerm;

  const { data } = await axios.get(`${apiVariable}/relayCodes`, {
    headers: { Authorization: `Bearer ${token}` },
    params,
  });
  return data;
};

const RelayPaymentsTable = ({ company }: { company: string }) => {
  const { token } = useAuth();
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const scrollAttached = useRef<boolean>(false);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    error,
  } = useInfiniteQuery({
    queryKey: ["relayCodes", company, searchTerm],
    queryFn: ({ pageParam = 0 }) =>
      fetchRelayCodes({
        token: token || "",
        company,
        offset: pageParam,
        limit: 20,
        searchTerm,
      }),
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === 20 ? allPages.length * 20 : undefined,

    enabled: !!token && !!company,
    initialPageParam: 0,
  });

  const rows = data?.pages.flat() || [];
  const gridRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const attachScrollListener = () => {
      const virtualScroller = gridRef.current?.querySelector(
        ".MuiDataGrid-virtualScroller",
      );
      if (virtualScroller && !scrollAttached.current) {
        const handleScroll = () => {
          const { scrollTop, scrollHeight, clientHeight } = virtualScroller;
          if (scrollHeight - scrollTop <= clientHeight + 50 && hasNextPage) {
            fetchNextPage();
          }
        };

        virtualScroller.addEventListener("scroll", handleScroll);
        scrollAttached.current = true;

        return () => {
          virtualScroller.removeEventListener("scroll", handleScroll);
          scrollAttached.current = false;
        };
      }
    };

    const timeout = setTimeout(attachScrollListener, 100);
    return () => clearTimeout(timeout);
  }, [fetchNextPage, hasNextPage, company]);

  return (
    <Box className="money-requests-table-container" height={900}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
        }}
      >
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by relevant fields"
          sx={{ maxWidth: "100%", width: 400, ml: "auto" }}
        />
      </Box>
      {isLoading ? (
        <Box className="table-loader">
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box className="table-error">
          <Typography color="error">Error: {error.message}</Typography>
        </Box>
      ) : (
        <Box ref={gridRef} height={700}>
          <PersistedDataGrid
            tableName="relayCodes"
            rows={rows}
            columns={relayPaymentsTableColumns(token, company)}
            loading={isFetchingNextPage}
            autoHeight={false}
            paginationMode="server"
            hideFooter
            sx={{ height: 700 }}
          />
        </Box>
      )}
    </Box>
  );
};

export default RelayPaymentsTable;
